import { registerConditions } from '../../core/conditions';
import { CondResult, HabitatTourMode, TourContext } from '../../types';

/**
 * Predicates for the habitat onboardings. They live here, beside the configs that use them,
 * rather than in `core/` — the tour core knows only `cond: string -> resolver` and never learns
 * what a camera or a schedule is.
 *
 * Every predicate answers `pending` until the habitat page has published its context. The page
 * publishes nothing until the habitat in the store matches the one in the URL and its schedule
 * query has settled, so `ctx.habitat` being present is the single readiness signal — no
 * predicate needs to re-derive it.
 */

/** The gate every other predicate is built on. */
const whenReady = <T>(ctx: TourContext | undefined, read: (ctx: TourContext) => T): T | 'pending' =>
  ctx?.habitat ? read(ctx) : 'pending';

/**
 * Like `whenReady`, but also waits on the explorer-offer eligibility query. `pending` until the
 * habitat is published AND eligibility has settled, so the ending never flashes the wrong branch
 * while the offer is still loading.
 */
const whenOfferKnown = (ctx: TourContext | undefined, read: (eligible: boolean, ctx: TourContext) => boolean): CondResult =>
  whenReady(
    ctx,
    (ready): CondResult =>
      ready.explorerOfferEligible === undefined ? 'pending' : read(ready.explorerOfferEligible, ready),
  );

const isMode =
  (mode: HabitatTourMode) =>
  (ctx: TourContext | undefined): CondResult =>
    whenReady(ctx, (ready) => ready.habitat!.mode === mode);

export const HABITAT_CONDITIONS = {
  habitatOnline: isMode('online'),
  habitatAsleep: isMode('asleep'),
  habitatOutage: isMode('outage'),

  /** The camera-angles step only applies to habitats with more than one camera. */
  hasMultipleCameras: (ctx: TourContext | undefined): CondResult =>
    whenReady(ctx, (ready) => ready.habitat!.cameraCount > 1),

  isFollowingHabitat: (ctx: TourContext | undefined): CondResult => whenReady(ctx, (ready) => ready.habitat!.isFollowing),
  isNotFollowingHabitat: (ctx: TourContext | undefined): CondResult =>
    whenReady(ctx, (ready) => !ready.habitat!.isFollowing),

  /**
   * 10A: the suggested habitat is one the user can already reach, so send them straight there.
   * A locked suggestion falls through to the coupon ending instead.
   */
  hasAccessibleRecommendedHabitat: (ctx: TourContext | undefined): CondResult =>
    whenReady(ctx, (ready) => Boolean(ready.recommendedHabitat?.accessible)),

  /**
   * There is a habitat to name at all (accessible or locked). Distinguishes the offer ending
   * that can name an animal from the bare fallback shown when nothing else exists.
   */
  hasRecommendedHabitat: (ctx: TourContext | undefined): CondResult =>
    whenReady(ctx, (ready) => Boolean(ready.recommendedHabitat)),

  /**
   * The ending branches for the explorer offer. Reached only after the accessible-recommended case
   * (10A) has already been tried, so a recommended habitat here is a locked one.
   *
   * - `canOfferCheckout`   — eligible and there is a locked habitat to buy: straight to Stripe.
   * - `canOfferNoRecommended` — eligible but nothing to name: send to the habitats list with the offer.
   * - `offerIneligible`    — cannot use the offer: just wrap the tour up.
   */
  canOfferCheckout: (ctx: TourContext | undefined): CondResult =>
    whenOfferKnown(ctx, (eligible, ready) => eligible && Boolean(ready.recommendedHabitat)),
  canOfferNoRecommended: (ctx: TourContext | undefined): CondResult =>
    whenOfferKnown(ctx, (eligible, ready) => eligible && !ready.recommendedHabitat),
  offerIneligible: (ctx: TourContext | undefined): CondResult => whenOfferKnown(ctx, (eligible) => !eligible),

  /** Welcome 1A: this is the first habitat they have ever opened. */
  isFirstHabitatVisit: (ctx: TourContext | undefined): CondResult =>
    whenReady(ctx, (ready) => ready.habitat!.visitedHabitatCount <= 1),

  /**
   * Selects the "existing user" welcome, which skips the introductions: an account older than a
   * day belongs to someone who has been here before, whatever they did on that first visit.
   */
  isExistingUser: (ctx: TourContext | undefined): CondResult => whenReady(ctx, (ready) => ready.habitat!.isExistingUser),
} as const;

registerConditions(HABITAT_CONDITIONS);

/** Narrow alias so configs cannot typo a condition name. */
export type HabitatCondition = keyof typeof HABITAT_CONDITIONS;
